import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tag,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Drawer,
  Empty,
  Badge,
  Tooltip,
  message,
  Modal,
  Progress,
  Row,
  Col,
  Table as AntTable,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import importApis from "../../api/WorkOrderManagement/ImportApis";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { render } from "@testing-library/react";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ImportRecord {
  paginationId: number;
  reportId: number;
  reportType: string;
  comments: string;
  requestedDate: string;
  requestedBy: number;
  filePath: string | null;
  uploadStatus: string;
  status:
    | "Requested"
    | "Processing"
    | "Error In File"
    | "Created Successfully"
    | "Error"
    | "Failed";
  totalRows: number;
  successCount: number;
  failureCount: number;
  createdDate: string;
  fileName?: string;
  uploadedBy?: string;
  errorReasons?: string;
  error?: string;
}

interface ConvertedRecord {
  [key: string]: any;
}

const ImportsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    type: undefined as string | undefined,
    status: undefined as string | undefined,
    dateRange: null as any,
  });
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Drawer for converted records
  const [recordsDrawerVisible, setRecordsDrawerVisible] = useState(false);
  const [selectedImport, setSelectedImport] = useState<ImportRecord | null>(
    null
  );
  const [convertedRecords, setConvertedRecords] = useState<ConvertedRecord[]>(
    []
  );
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [recordsPagination, setRecordsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // Auto-refresh for processing imports
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const handleDelete = (record: ImportRecord) => {
    Swal.fire({
      title: "Delete Import",
      text: `Are you sure you want to delete this import (Report ID: ${record.reportId})?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response: any = await importApis.deleteImport(
            record.reportId.toString()
          );
          if (response?.status) {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Import deleted successfully",
              confirmButtonColor: "#3085d6",
            });
            setRefreshTrigger(!refreshTrigger);
          } else {
            Swal.fire({
              icon: "error",
              title: "Error!",
              text: response?.message || "Failed to delete import",
              confirmButtonColor: "#d33",
            });
          }
        } catch (error) {
          console.error("Error deleting import:", error);
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "Failed to delete import",
            confirmButtonColor: "#d33",
          });
        }
      }
    });
  };

  const handleDownload = async (record: ImportRecord) => {
    try {
      if (record.filePath) {
        window.open(record.filePath, "_blank");
      } else {
        message.warning("No file available for download");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      message.error("Failed to download file");
    }
  };

  const handleViewRecords = async (record: ImportRecord) => {
    setSelectedImport(record);
    setRecordsDrawerVisible(true);
    // Reset pagination when opening new record
    setRecordsPagination({
      current: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
    });
    fetchConvertedRecords(record.reportId, 1, 10);
  };

  const fetchConvertedRecords = async (
    reportId: number,
    pageNo: number,
    size: number
  ) => {
    setLoadingRecords(true);

    try {
      // Call API to get converted JSON list with pagination params
      const response: any = await importApis.getConvertedJsonList({
        reportId: reportId,
        pageNo: pageNo,
        size: size,
      });

      if (response?.status && response.data?.convertedJson) {
        const records = response.data.convertedJson;
        const pagination = response.data.pagination;

        setConvertedRecords(records);

        // Update pagination state from API response
        if (pagination) {
          setRecordsPagination({
            current: pagination.currentPage || pageNo,
            pageSize: pagination.pageSize || size,
            total: pagination.totalRecords || 0,
            totalPages: pagination.totalPages || 0,
          });
        }

        // Auto-expand all failed rows by default
        const failedRowKeys = records
          .map((record: any, index: number) => {
            if (
              (record.status === "Failed" || record.status === "Error") &&
              record.Error
            ) {
              return index.toString();
            }
            return null;
          })
          .filter((key: any) => key !== null);

        setExpandedRowKeys(failedRowKeys);
      } else {
        message.error("Failed to load records");
        setConvertedRecords([]);
        setExpandedRowKeys([]);
      }
    } catch (error) {
      console.error("Error fetching converted records:", error);
      message.error("Failed to load records");
      setConvertedRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleRecordsPaginationChange = (page: number, pageSize: number) => {
    if (selectedImport) {
      setRecordsPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize,
      }));
      fetchConvertedRecords(selectedImport.reportId, page, pageSize);
    }
  };

  const handleCreate = async (record: ImportRecord) => {
    Swal.fire({
      title: "Create Records",
      text: `Are you sure you want to create records from this import (Report ID: ${record.reportId})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Create",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response: any = await importApis.processExcelCreation(
            record.reportId
          );

          if (response?.status) {
            Swal.fire({
              icon: "success",
              title: "Success!",
              text: "Records created successfully!",
              confirmButtonColor: "#3085d6",
            });
            // Trigger refresh
            setRefreshTrigger(!refreshTrigger);
          } else {
            Swal.fire({
              icon: "error",
              title: "Error!",
              text: response?.message || "Failed to create records",
              confirmButtonColor: "#d33",
            });
          }
        } catch (error: any) {
          console.error("Error creating records:", error);
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: error?.response?.data?.message || "Failed to create records",
            confirmButtonColor: "#d33",
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const resetFilters = () => {
    setFilters({
      type: undefined,
      status: undefined,
      dateRange: null,
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be handled by TransactionFlowDefaultTemplate
  };

  const getStatusTag = (status: any) => {
    const statusConfig: any = {
      Requested: {
        color: "warning",
        icon: <ClockCircleOutlined />,
        text: "Requested",
      },
      Processed: {
        color: "processing",
        icon: <SyncOutlined />,
        text: "Processed",
      },
      Processing: {
        color: "processing",
        icon: <SyncOutlined spin />,
        text: "Processing",
      },
      "Error In File": {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Error In File",
      },
      "Created Successfully": {
        color: "success",
        icon: <CheckCircleOutlined />,
        text: "Created Successfully",
      },
      Validated: {
        color: "success",
        icon: <CheckCircleOutlined />,
        text: "Validated",
      },
      Error: {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Error",
      },
      Failed: {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Failed",
      },
    };
    console.log(status);
    const config = statusConfig[status];
    return (
      <Tag icon={config?.icon} color={config?.color}>
        {config?.text}
      </Tag>
    );
  };

  const getTypeTag = (type: string) => {
    const typeMap: any = {
      materialCreate: { text: "Material", color: "blue" },
      supplierCreate: { text: "Supplier", color: "green" },
      "Contractor RA Bills": { text: "Contractor RA Bills", color: "purple" },
      "Client RA Bills": { text: "Client RA Bills", color: "purple" },
      "Contractor Payments": { text: "Contractor Payments", color: "orange" },
      "Client Receivables": { text: "Client Receivables", color: "orange" },
    };

    const typeInfo = typeMap[type] || { text: type, color: "default" };
    return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
  };

  const columns: any = [
    {
      title: "Report ID",
      dataIndex: "reportId",
      key: "reportId",
      width: 100,
    },
    {
      title: "Type",
      dataIndex: "reportType",
      key: "reportType",
      width: 150,
      render: (type: string) => getTypeTag(type),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: any, record: ImportRecord) => {
        // Show tooltip only if error exists and is not null
        if (record.error && (status === "Failed" || status === "Error")) {
          // Clean up error message - handle JSON-encoded strings with escaped quotes
          let errorMessage = record.error;
          try {
            // Try to parse as JSON string first (handles cases like "\"Record with id 921 not found\"")
            errorMessage = JSON.parse(errorMessage);
          } catch {
            // If not valid JSON, clean up escaped quotes manually
            errorMessage = errorMessage
              .replace(/^"/, "") // Remove leading quote
              .replace(/"$/, "") // Remove trailing quote
              .replace(/\\"/g, '"'); // Replace escaped quotes with regular quotes
          }
          return <Tooltip title={errorMessage}>{getStatusTag(status)}</Tooltip>;
        }
        return getStatusTag(status);
      },
    },
    {
      title: "Requested Date",
      dataIndex: "requestedDate",
      key: "requestedDate",
      width: 180,
      render: (date: string) => dayjs(date).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "Records",
      key: "records",
      width: 200,
      render: (_: any, record: ImportRecord) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Total: {record.totalRows}</span>
          </div>
          {record.status !== "Requested" && record.status !== "Processing" && (
            <>
              <Progress
                percent={
                  record.totalRows > 0
                    ? Math.round((record.successCount / record.totalRows) * 100)
                    : 0
                }
                size="small"
                status={record.failureCount > 0 ? "exception" : "success"}
                format={() => `${record.successCount}/${record.totalRows}`}
              />
              {record.failureCount > 0 && (
                <span style={{ color: "#ff4d4f", fontSize: "12px" }}>
                  {record.failureCount} error(s)
                </span>
              )}
            </>
          )}
        </Space>
      ),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 150,
      render: (text: string) => (
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 130,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_: any, record: any) => (
        <Space>
          {record.filePath && (
            <Tooltip title="Download File">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          )}
          {/* {(record.status === "Error" || record.status === "Validated" ||
            record.status === "Processed") && ( */}
          <Tooltip title="View Records">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewRecords(record)}
            />
          </Tooltip>
          {/* )} */}
          {record.uploadStatus === "Preview" &&
            record.status === "Validated" && (
              <Tooltip title="Create Records">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleCreate(record)}
                >
                  Create
                </Button>
              </Tooltip>
            )}
          {record.status === "Error" && record.errorReasons && (
            <Tooltip title={record.errorReasons}>
              <Button type="text" danger icon={<InfoCircleOutlined />} />
            </Tooltip>
          )}
          {record.status !== "Processing" && (
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Generate columns based on report type
  const generateRecordColumns = () => {
    if (!selectedImport) return [];

    const { reportType } = selectedImport;

    // RA Bills columns
    if (
      reportType === "Contractor RA Bills" ||
      reportType === "Client RA Bills"
    ) {
      return [
        {
          title: "Site Name",
          dataIndex: "siteName",
          key: "siteName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Work Order Number",
          dataIndex: "workOrderNumber",
          key: "workOrderNumber",
          width: 150,
          ellipsis: true,
        },
        {
          title:
            reportType === "Client RA Bills"
              ? "Client Name"
              : "Contractor Name",
          dataIndex:
            reportType === "Client RA Bills" ? "clientName" : "contractorName",
          key:
            reportType === "Client RA Bills" ? "clientName" : "contractorName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "RA Bill #",
          dataIndex: "raBillNumber",
          key: "raBillNumber",
          width: 120,
          ellipsis: true,
        },
        {
          title: "RA Bill Date",
          dataIndex: "raBillDate",
          key: "raBillDate",
          width: 120,
          render: (date: string) =>
            date ? dayjs(date).format("DD MMM YYYY") : "-",
        },
        {
          title: "Work Execution Start Date",
          dataIndex: "executionStartDate",
          key: "executionStartDate",
          width: 180,
          render: (date: string) =>
            date ? dayjs(date).format("DD MMM YYYY") : "-",
        },
        {
          title: "Work Execution End Date",
          dataIndex: "executionEndDate",
          key: "executionEndDate",
          width: 180,
          render: (date: string) =>
            date ? dayjs(date).format("DD MMM YYYY") : "-",
        },
        {
          title: "Due Date",
          dataIndex: "paymentDueDate",
          key: "paymentDueDate",
          width: 120,
          render: (date: string) =>
            date ? dayjs(date).format("DD MMM YYYY") : "-",
        },
        {
          title: "Work Category",
          dataIndex: "workCategoryName",
          key: "workCategoryName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Task Title",
          dataIndex: "taskTitle",
          key: "taskTitle",
          width: 200,
          ellipsis: true,
        },
        {
          title: "Stage Title",
          dataIndex: "stageTitle",
          key: "stageTitle",
          width: 150,
          ellipsis: true,
        },
        {
          title: "UOM",
          dataIndex: "uomName",
          key: "uomName",
          width: 100,
        },
        {
          title: "Executed Quantity",
          dataIndex: "executedQuantity",
          key: "executedQuantity",
          width: 150,
          align: "right" as const,
        },
        {
          title: "Executed Amount",
          dataIndex: "executedAmount",
          key: "executedAmount",
          width: 150,
          align: "right" as const,
          render: (amount: number) =>
            amount ? `₹${amount?.toLocaleString()}` : "-",
        },
        {
          title: "Rate",
          dataIndex: "rate",
          key: "rate",
          width: 120,
          align: "right" as const,
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          width: 200,
          ellipsis: true,
          render: (text: string) =>
            text ? (
              <Tooltip title={text}>
                <Tag color={text === "Failed" ? "error" : "success"}>
                  {text}
                </Tag>
              </Tooltip>
            ) : (
              "-"
            ),
        },
      ];
    }

    // Payments columns
    if (
      reportType === "Contractor Payments" ||
      reportType === "Client Receivables"
    ) {
      return [
        {
          title: "Site Name",
          dataIndex: "siteName",
          key: "siteName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "WO Number",
          dataIndex: "workOrderNumber",
          key: "workOrderNumber",
          width: 150,
          ellipsis: true,
        },
        {
          title:
            reportType === "Client Receivables"
              ? "Client Name"
              : "Contractor Name",
          dataIndex:
            reportType === "Client Receivables"
              ? "clientName"
              : "contractorName",
          key:
            reportType === "Client Receivables"
              ? "clientName"
              : "contractorName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Payment #",
          dataIndex: "raBillPaymentSequenceId",
          key: "raBillPaymentSequenceId",
          width: 120,
          ellipsis: true,
        },
        {
          title: "Payment Type",
          dataIndex: "paymentType",
          key: "paymentType",
          width: 120,
        },
        {
          title: "RA Bill #",
          dataIndex: "raBillSequenceId",
          key: "raBillSequenceId",
          width: 120,
          ellipsis: true,
        },
        {
          title: "Payment Date",
          dataIndex: "paymentDate",
          key: "paymentDate",
          width: 120,
          render: (date: string) =>
            date ? dayjs(date).format("DD MMM YYYY") : "-",
        },
        {
          title: "Bill Amount",
          dataIndex: "netPayable",
          key: "netPayable",
          width: 120,
          align: "right" as const,
          render: (amount: number) => `₹${amount?.toLocaleString()}`,
        },
        {
          title: "Amount Paid",
          dataIndex: "paidAmount",
          key: "paidAmount",
          width: 120,
          align: "right" as const,
          render: (amount: number) => `₹${amount?.toLocaleString()}`,
        },
        {
          title: "Mode of Payment",
          dataIndex: "paymentMethod",
          key: "paymentMethod",
          width: 150,
        },
        {
          title: "Bank Name",
          dataIndex: "bankName",
          key: "bankName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Reference #",
          dataIndex: "paymentReference",
          key: "paymentReference",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Narration",
          dataIndex: "narration",
          key: "narration",
          width: 200,
          ellipsis: true,
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          width: 200,
          ellipsis: true,
          render: (text: string) =>
            text ? (
              <Tooltip title={text}>
                <Tag color={text === "Failed" ? "error" : "success"}>
                  {text}
                </Tag>
              </Tooltip>
            ) : (
              "-"
            ),
        },
      ];
    }
  };

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="imports-list"
        title="Bulk Upload Imports"
        subtitle="View and manage all bulk uploaded files for RA Bills and Payments"
        columns={columns}
        loading={loading}
        refresh={refreshTrigger}
        topRightJSX={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setRefreshTrigger(!refreshTrigger)}
          >
            Refresh
          </Button>
        }
        extra={{
          tableScrollX: 1200,
          showSearch: true,
          searchPlaceholder: "Search by report ID or type...",
          advancedFilter: {
            visible: drawerVisible,
            onClose: () => setDrawerVisible(false),
            onOpen: () => setDrawerVisible(true),
            content: (
              <div className="p-4">
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <label className="form-label">Type</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Filter by Type"
                      allowClear
                      value={filters.type}
                      onChange={(value) =>
                        setFilters({ ...filters, type: value })
                      }
                    >
                      <Option value="materialCreate">Material</Option>
                      <Option value="supplierCreate">Supplier</Option>
                      <Option value="Contractor RA Bills">
                        Contractor RA Bills
                      </Option>
                      <Option value="Client RA Bills">Client RA Bills</Option>
                      <Option value="Contractor Payments">
                        Contractor Payments
                      </Option>
                      <Option value="Client Receivables">
                        Client Receivables
                      </Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Status</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Filter by Status"
                      allowClear
                      value={filters.status}
                      onChange={(value) =>
                        setFilters({ ...filters, status: value })
                      }
                    >
                      <Option value="Requested">Requested</Option>
                      <Option value="Processing">Processing</Option>
                      <Option value="Error In File">Error In File</Option>
                      <Option value="Created Successfully">
                        Created Successfully
                      </Option>
                      <Option value="Error">Error</Option>
                    </Select>
                  </Col>
                  <Col span={24}>
                    <label className="form-label">Requested Date Range</label>
                    <RangePicker
                      style={{ width: "100%" }}
                      value={filters.dateRange}
                      onChange={(dates) =>
                        setFilters({ ...filters, dateRange: dates })
                      }
                      placeholder={["From Date", "To Date"]}
                      format="DD MMM YYYY"
                    />
                  </Col>
                </Row>
                <div className="mt-4 d-flex gap-2">
                  <Button
                    type="primary"
                    onClick={applyFilters}
                    style={{ flex: 1 }}
                  >
                    Apply Filters
                  </Button>
                  <Button onClick={resetFilters} style={{ flex: 1 }}>
                    Reset
                  </Button>
                </div>
              </div>
            ),
          },
        }}
      />

      {/* Records Drawer */}
      <Drawer
        title={
          <Space>
            {/* <EyeOutlined style={{ color: "#1890ff" }} /> */}
            <span>
              Imported Records - Report #{selectedImport?.reportId} (
              {selectedImport?.reportType})
            </span>
          </Space>
        }
        placement="right"
        width="80%"
        open={recordsDrawerVisible}
        onClose={() => {
          setRecordsDrawerVisible(false);
          setConvertedRecords([]);
          setSelectedImport(null);
          setExpandedRowKeys([]);
          setRecordsPagination({
            current: 1,
            pageSize: 10,
            total: 0,
            totalPages: 0,
          });
        }}
      >
        {loadingRecords ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <SyncOutlined spin style={{ fontSize: 24 }} />
            <p>Loading records...</p>
          </div>
        ) : convertedRecords.length > 0 ? (
          <div>
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: 4,
              }}
            >
              <p style={{ margin: 0, fontWeight: 500 }}>
                Total Records:{" "}
                {recordsPagination.total ||
                  selectedImport?.totalRows ||
                  convertedRecords.length}
              </p>
              {selectedImport && (
                <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                  Success: {selectedImport.successCount} | Failed:{" "}
                  {selectedImport.failureCount}
                </p>
              )}
            </div>

            <AntTable
              columns={generateRecordColumns()}
              dataSource={convertedRecords}
              loading={loadingRecords}
              pagination={{
                current: recordsPagination.current,
                pageSize: recordsPagination.pageSize,
                total: recordsPagination.total,
                showSizeChanger: true,
                showQuickJumper: false,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} records`,
                onChange: handleRecordsPaginationChange,
                onShowSizeChange: handleRecordsPaginationChange,
              }}
              scroll={{ x: "max-content" }}
              size="small"
              rowKey={(record, index) => index?.toString() || "0"}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpandedRowsChange: (keys: any) => setExpandedRowKeys(keys),
                expandedRowRender: (record: any) => {
                  // Only show expanded content if status is Failed and errors exist
                  if (record.status !== "Failed" && record.status !== "Error") {
                    return null;
                  }

                  // Get error messages from the record
                  const errorMessages: string[] = [];

                  if (record.Error) {
                    try {
                      // If error is a JSON array string
                      const parsedErrors = JSON.parse(record.Error);
                      if (Array.isArray(parsedErrors)) {
                        errorMessages.push(...parsedErrors);
                      } else {
                        errorMessages.push(record.Error);
                      }
                    } catch {
                      // If error is a plain string or already an array
                      if (Array.isArray(record.Error)) {
                        errorMessages.push(...record.Error);
                      } else {
                        errorMessages.push(record.Error);
                      }
                    }
                  }

                  if (errorMessages.length === 0) {
                    return (
                      <div style={{ padding: "12px", color: "#999" }}>
                        No error details available
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{ padding: "12px 24px", background: "#fff2f0" }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <strong style={{ color: "#cf1322" }}>
                          Validation Errors:
                        </strong>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {errorMessages.map((error, index) => (
                          <li
                            key={index}
                            style={{
                              color: "#cf1322",
                              marginBottom: 4,
                              fontSize: 13,
                            }}
                          >
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                },
                rowExpandable: (record: any) => {
                  // Only allow expansion for Failed/Error status
                  return (
                    (record.status === "Failed" || record.status === "Error") &&
                    !!record.Error
                  );
                },
                expandIcon: ({ expanded, onExpand, record }) => {
                  // Only show expand icon for Failed/Error records with errors
                  if (
                    (record.status === "Failed" || record.status === "Error") &&
                    record.Error
                  ) {
                    return (
                      <span
                        onClick={(e) => onExpand(record, e)}
                        style={{
                          cursor: "pointer",
                          color: "#cf1322",
                          fontSize: "16px",
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "0 8px",
                        }}
                      >
                        {expanded ? (
                          <CloseCircleOutlined />
                        ) : (
                          <InfoCircleOutlined />
                        )}
                      </span>
                    );
                  }
                  return (
                    <span style={{ width: 24, display: "inline-block" }}></span>
                  );
                },
              }}
            />
          </div>
        ) : (
          <Empty description="No records found" />
        )}
      </Drawer>
    </>
  );
};

export default ImportsList;
